/**
 * Exposes the host app's shared module instances on window globals so that
 * runtime-loaded library bundles (built by cad-lib-bundler) can resolve their
 * externalized imports against the exact singletons the host runs.
 *
 * This file is the host half of cad-lib-bundler/globals-contract.json
 * (schema GLOBALS_SCHEMA below). When the contract changes — a package is
 * added/removed from the externals, a global is renamed — bump the schema in
 * BOTH places in the same PR. The version loader refuses bundles built
 * against a newer schema than the host supports.
 *
 * Nothing here loads until a version override is active:
 * exposeLibraryGlobals() performs all module access via dynamic import, and
 * Rollup resolves those into the same chunks the app already ships.
 */

export const GLOBALS_SCHEMA = 1;

type ModuleNamespace = Record<string, unknown>;
type PkgNamespaces = Record<string, Record<string, ModuleNamespace>>;

declare global {
  interface Window {
    __ergeonHostLibs?: ModuleNamespace;
    __ergeonLibEnv?: Record<string, string | undefined>;
    __ergeonLibOverrides?: Record<
      string,
      {name: string; version: string; globalsSchema: number; modules: Record<string, ModuleNamespace>}
    >;
    __ergeonLibResolve?: (pkg: string, key: string) => ModuleNamespace;
  }
}

/**
 * Vite's dynamic-import namespaces have no `__esModule` marker, so webpack's
 * CommonJS interop in the loaded bundles would treat the whole namespace as
 * the default export. Wrap so both `import X from` and `import * as X from`
 * sites in bundles resolve correctly.
 */
export const toInterop = (ns: ModuleNamespace): ModuleNamespace => ({
  ...ns,
  default: (ns as {default?: unknown}).default ?? ns,
  __esModule: true,
});

const interopAll = (entries: Record<string, unknown>): Record<string, ModuleNamespace> =>
  Object.fromEntries(
    Object.entries(entries).map(([key, ns]) => [key, toInterop(ns as ModuleNamespace)]),
  );

const loadSharedModules = async (): Promise<Record<string, ModuleNamespace>> => {
  const [react, reactDom, reactDomClient, jsxRuntime, reactRouterDom] = await Promise.all([
    import('react'),
    import('react-dom'),
    import('react-dom/client'),
    import('react/jsx-runtime'),
    import('react-router-dom'),
  ]);
  const [three, threeTsl, threeWebgpu, axios, lodash, tween, sentryBrowser] = await Promise.all([
    import('three'),
    import('three/tsl'),
    import('three/webgpu'),
    import('axios'),
    import('lodash'),
    import('@tweenjs/tween.js'),
    import('@sentry/browser'),
  ]);
  return interopAll({
    react,
    reactDom,
    reactDomClient,
    jsxRuntime,
    reactRouterDom,
    three,
    threeTsl,
    threeWebgpu,
    axios,
    lodash,
    tween,
    sentryBrowser,
  });
};

/**
 * Every module in the built-in 3d-lib's exports/ directory, keyed the same
 * way the bundler's externals emit them — a loaded draft-editor bundle may
 * deep-import any of them. A unit test keeps this list in sync with
 * node_modules/@ergeon/3d-lib/exports. There is no '.' entry: the published
 * package's main (src/index.ts) doesn't exist — 3d-lib is only consumable
 * through exports/* subpaths.
 */
const load3dLibNamespaces = async (): Promise<Record<string, ModuleNamespace>> =>
  interopAll({
    'exports/AddressDialog': await import('@ergeon/3d-lib/exports/AddressDialog'),
    'exports/AnnotationTool': await import('@ergeon/3d-lib/exports/AnnotationTool'),
    'exports/CatalogBrowser': await import('@ergeon/3d-lib/exports/CatalogBrowser'),
    'exports/DemoWrapper': await import('@ergeon/3d-lib/exports/DemoWrapper'),
    'exports/ErrorButtons': await import('@ergeon/3d-lib/exports/ErrorButtons'),
    'exports/FullScreenTool': await import('@ergeon/3d-lib/exports/FullScreenTool'),
    'exports/hooks': await import('@ergeon/3d-lib/exports/hooks'),
    'exports/InventoryGallery': await import('@ergeon/3d-lib/exports/InventoryGallery'),
    'exports/MaterialList': await import('@ergeon/3d-lib/exports/MaterialList'),
    'exports/MaterialListProductItem': await import('@ergeon/3d-lib/exports/MaterialListProductItem'),
    'exports/permissions': await import('@ergeon/3d-lib/exports/permissions'),
    'exports/PreviewGenerator': await import('@ergeon/3d-lib/exports/PreviewGenerator'),
    'exports/pricing': await import('@ergeon/3d-lib/exports/pricing'),
    'exports/ProductConfigTableService': await import('@ergeon/3d-lib/exports/ProductConfigTableService'),
    'exports/Questionnaire': await import('@ergeon/3d-lib/exports/Questionnaire'),
    'exports/Questionnaire/constants': await import('@ergeon/3d-lib/exports/Questionnaire/constants'),
    'exports/Questionnaire/utils': await import('@ergeon/3d-lib/exports/Questionnaire/utils'),
    'exports/RulesGallery': await import('@ergeon/3d-lib/exports/RulesGallery'),
    'exports/Sandbox': await import('@ergeon/3d-lib/exports/Sandbox'),
    'exports/Shortener': await import('@ergeon/3d-lib/exports/Shortener'),
    'exports/StaticMapViewer': await import('@ergeon/3d-lib/exports/StaticMapViewer'),
    'exports/StyleBrowser': await import('@ergeon/3d-lib/exports/StyleBrowser'),
    'exports/StyleBrowserTypes': await import('@ergeon/3d-lib/exports/StyleBrowserTypes'),
    'exports/StyleEditor': await import('@ergeon/3d-lib/exports/StyleEditor'),
    'exports/StyleSelector': await import('@ergeon/3d-lib/exports/StyleSelector'),
    'exports/StyleSelector/types': await import('@ergeon/3d-lib/exports/StyleSelector/types'),
    'exports/StyleSelectorPopup': await import('@ergeon/3d-lib/exports/StyleSelectorPopup'),
    'exports/Summable': await import('@ergeon/3d-lib/exports/Summable'),
    'exports/types': await import('@ergeon/3d-lib/exports/types'),
    'exports/urlManager': await import('@ergeon/3d-lib/exports/urlManager'),
    'exports/utils': await import('@ergeon/3d-lib/exports/utils'),
    'exports/WebGLContextError': await import('@ergeon/3d-lib/exports/WebGLContextError'),
  });

/**
 * Namespace maps for the externalized @ergeon packages, keyed by the same
 * subpath keys the bundler's externals emit ('.', 'exports/<name>', ...).
 */
const loadPkgNamespaces = async (): Promise<PkgNamespaces> => ({
  cad: interopAll({'.': await import('@ergeon/cad')}),
  'core-components': interopAll({
    '.': await import('@ergeon/core-components'),
    'exports/MarkdownRenderer': await import('@ergeon/core-components/exports/MarkdownRenderer'),
  }),
  'erg-auth': interopAll({'.': await import('@ergeon/erg-auth')}),
  'erg-catalog-rules': interopAll({'.': await import('@ergeon/erg-catalog-rules')}),
  'erg-utils-js': interopAll({
    '.': await import('@ergeon/erg-utils-js'),
    'exports/sentry/reporting': await import('@ergeon/erg-utils-js/exports/sentry/reporting'),
  }),
  'file-storage': interopAll({'.': await import('@ergeon/file-storage')}),
  'map-api-wrapper': interopAll({'.': await import('@ergeon/map-api-wrapper')}),
  'pricing-lib': interopAll({
    '.': await import('@ergeon/pricing-lib'),
    'exports/quote-line': await import('@ergeon/pricing-lib/exports/quote-line'),
    'exports/s3-resources': await import('@ergeon/pricing-lib/exports/s3-resources'),
  }),
  '3d-lib': await load3dLibNamespaces(),
});

// The env values the host's own build inlined (Vite define). Loaded bundles
// read process.env.X as window.__ergeonLibEnv.X — see cad-lib-bundler.
//
// Each read must be a LITERAL process.env.X: Vite's define only replaces
// literal member expressions, so process.env[key] in a loop would resolve to
// undefined at runtime. The keys are therefore spelled out; the sync test in
// libraryGlobals.test.ts pins Object.keys(buildLibEnv()) against the contract's
// envKeys, so drift between this list, vite.config.ts's define block, and
// globals-contract.json fails CI — the failure mode that lost FENCE_LAYOUT_API_URL.
const buildLibEnv = (): Record<string, string | undefined> => ({
  NODE_ENV: process.env.NODE_ENV,
  APP_ENV: process.env.APP_ENV,
  API_HOST: process.env.API_HOST,
  CAD_SCREENSHOT_URL: process.env.CAD_SCREENSHOT_URL,
  CATALOG_RULES_URL: process.env.CATALOG_RULES_URL,
  CATALOG_AUTO_RULES_URL: process.env.CATALOG_AUTO_RULES_URL,
  COPY_FILE_ASYNC_SERVICE: process.env.COPY_FILE_ASYNC_SERVICE,
  CURRENT_CATALOG_VERSION_URL: process.env.CURRENT_CATALOG_VERSION_URL,
  GLOBAL_CATALOG_URL: process.env.GLOBAL_CATALOG_URL,
  GOOGLEAPPS_INTEGRATION_3D_TILES_URL: process.env.GOOGLEAPPS_INTEGRATION_3D_TILES_URL,
  GOOGLEAPPS_INTEGRATION_AUTH_TOKEN: process.env.GOOGLEAPPS_INTEGRATION_AUTH_TOKEN,
  GOOGLEAPPS_INTEGRATION_CALL_METHOD_BASE_URL: process.env.GOOGLEAPPS_INTEGRATION_CALL_METHOD_BASE_URL,
  IMAGE_CACHE_S3_BUCKET_URL: process.env.IMAGE_CACHE_S3_BUCKET_URL,
  INVENTORY_ITEMS_URL: process.env.INVENTORY_ITEMS_URL,
  ERGEON_HOME: process.env.ERGEON_HOME,
  GOOGLE_MAPS_API_KEY: process.env.GOOGLE_MAPS_API_KEY,
  RESOURCE_BASE_S3_BUCKET_URL: process.env.RESOURCE_BASE_S3_BUCKET_URL,
  SCREENSHOT_TO_S3_SERVICE: process.env.SCREENSHOT_TO_S3_SERVICE,
  CATALOG_DATA_URL: process.env.CATALOG_DATA_URL,
  CATALOG_VERSION_CURRENT_URL: process.env.CATALOG_VERSION_CURRENT_URL,
  CATALOG_VERSION_CONTENT_URL: process.env.CATALOG_VERSION_CONTENT_URL,
  SITE_PLAN_FAST_URL: process.env.SITE_PLAN_FAST_URL,
  CENTRIFUGO_URL: process.env.CENTRIFUGO_URL,
  FENCE_LAYOUT_API_URL: process.env.FENCE_LAYOUT_API_URL,
});

/** The env keys buildLibEnv exposes — asserted against the contract in tests. */
export const libEnvKeys = (): string[] => Object.keys(buildLibEnv());

const lookupOverride = (pkg: string, key: string): ModuleNamespace | undefined =>
  window.__ergeonLibOverrides?.[pkg]?.modules?.[key];

const missingModuleError = (pkg: string, key: string): Error =>
  new Error(
    `[libraryGlobals] Cannot resolve @ergeon/${pkg} "${key}": not provided by the host ` +
      'and no override bundle supplies it. The selected library version may be incompatible ' +
      'with this build of cad.ergeon.in.',
  );

const makeResolve =
  (pkgs: PkgNamespaces) =>
    (pkg: string, key: string): ModuleNamespace => {
      const override = lookupOverride(pkg, key);
      if (override) return override;
      const builtIn = pkgs[pkg]?.[key];
      if (!builtIn) throw missingModuleError(pkg, key);
      return builtIn;
    };

const doExpose = async (): Promise<void> => {
  const [shared, pkgs] = await Promise.all([loadSharedModules(), loadPkgNamespaces()]);
  window.__ergeonHostLibs = {...shared, pkgs};
  window.__ergeonLibEnv = buildLibEnv();
  window.__ergeonLibResolve = makeResolve(pkgs);
};

let exposed: Promise<void> | null = null;

export const exposeLibraryGlobals = (): Promise<void> => {
  if (!exposed) exposed = doExpose();
  return exposed;
};
